import type { TFillRef } from "@libs/figure-spans";
import { fillFigureText, markFigureSpans, type TFigureLookup } from "@/zapi-frontend/shared/lib/figureRefs";
import type { TLeoChatMessage, TLeoChatTaskState } from "@/zapi-frontend/stores/leoChatStore";
import type { TActiveLeoChatTask, TLeoChatHistoryItem } from "../api/leoChat";

export const historyMessages = (items: TLeoChatHistoryItem[]): TLeoChatMessage[] =>
  items.map((item) => ({
    role: item.role,
    content: item.content,
    isError: item.isError,
    appliedChapters: item.appliedChapters ?? undefined,
    estCostUsd: item.estCostUsd,
    ...(item.role === "assistant" && item.refs?.length ? { refs: item.refs } : {})
  }));

export const replySlots = (message: TLeoChatMessage): TFillRef[] | undefined =>
  message.role === "assistant" && message.refs?.length ? message.refs : undefined;

export const replyMarkdown = (message: TLeoChatMessage, figures: TFigureLookup): string => {
  const refs = replySlots(message);
  if (!refs) return message.content;
  const filled = fillFigureText(message.content, refs, figures);
  return markFigureSpans(filled.text, filled.refs, figures);
};

const PLATFORM_FOLLOW_UP = /^\[Follow-up from the platform(?:\s+(\d+\/\d+))?\]\s*/;

export type TPlatformFollowUp = { depth: string | null; text: string };

export const platformFollowUp = (message: TLeoChatMessage): TPlatformFollowUp | null => {
  const match = PLATFORM_FOLLOW_UP.exec(message.content);
  return match ? { depth: match[1] ?? null, text: message.content.slice(match[0].length) } : null;
};

export type TReaderMessage = { message: TLeoChatMessage; chaptersOnly: boolean };

const PLATFORM_NOTE =
  /^(?:Checks that changed: |Kept as the ledger has it|Written back to the ledger's value |Named for the re-read |The platform starts |Follow-up turn \d)/;

const readerContent = (content: string) =>
  content
    .split("\n\n")
    .filter((paragraph, index) => index === 0 || !PLATFORM_NOTE.test(paragraph))
    .join("\n\n");

const forReader = (message: TLeoChatMessage): TLeoChatMessage =>
  message.role === "assistant" ? { ...message, content: readerContent(message.content) } : message;

export const readerMessages = (messages: TLeoChatMessage[]): TReaderMessage[] => {
  const shown: TReaderMessage[] = [];
  let inFollowUp = false;
  for (const message of messages) {
    if (message.role === "user") {
      inFollowUp = platformFollowUp(message) !== null;
      if (!inFollowUp) shown.push({ message, chaptersOnly: false });
    } else if (!inFollowUp) {
      shown.push({ message: forReader(message), chaptersOnly: false });
    } else if (message.appliedChapters?.length) {
      shown.push({ message, chaptersOnly: true });
    }
  }
  return shown;
};

export const followedTask = (task: TActiveLeoChatTask & { id: string }): TLeoChatTaskState => ({
  id: task.id,
  canEdit: task.canEdit ?? false,
  status: "running",
  progressText: null,
  startedAt: task.createdAt ? Date.parse(task.createdAt) : Date.now(),
  liveStatus: null,
  editedChapters: [],
  editedHome: false
});
